import { styled } from '@material-ui/core';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { useGeolocation } from '../../../../../hooks/useGeolocation';
import { domTag } from '../../../../utils/styledFilter';
import { type ILocationDetailsSchema } from '../helper';

/** Metadata about the fix that the form does not carry (only lat/lng are posted). */
export interface GeoFixMeta {
	accuracy: number | null;
	capturedAt: string | null;
}

interface GeolocationCaptureProps {
	hookForm: UseFormReturn<ILocationDetailsSchema>;
	/**
	 * True once a polling unit is chosen. The map card below then carries the
	 * meaning of the fix, so this card shrinks to a one-line confirmation
	 * instead of repeating raw coordinates.
	 */
	hasPollingUnit?: boolean;
	/** Reports accuracy / capture time so the card below can caveat a coarse fix. */
	onFixChange?: (meta: GeoFixMeta) => void;
}

const formatCoordinate = (value: number, type: 'lat' | 'lng'): string => {
	const absolute = Math.abs(value);
	const direction = type === 'lat' ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
	return `${absolute.toFixed(6)}° ${direction}`;
};

export const formatAccuracy = (accuracy: number | null): string =>
	accuracy === null ? '' : ` · accuracy ±${Math.round(accuracy)} m`;

// This is the ONE caller of useGeolocation on the step — a second call would be
// a second permission prompt. Everything else reads the fix from the form.
const GeolocationCapture: React.FC<GeolocationCaptureProps> = ({ hookForm, hasPollingUnit = false, onFixChange }) => {
	const { latitude, longitude, accuracy, capturedAt, loading, error, retry } = useGeolocation();

	// Sync coordinates to form when available
	useEffect(() => {
		if (latitude !== null && longitude !== null) {
			hookForm.setValue('latitude', latitude, { shouldValidate: true });
			hookForm.setValue('longitude', longitude, { shouldValidate: true });
		}
	}, [latitude, longitude, hookForm]);

	// Parent callbacks are new references each render; hold the latest one.
	const onFixChangeRef = useRef(onFixChange);
	onFixChangeRef.current = onFixChange;
	useEffect(() => {
		onFixChangeRef.current?.({ accuracy, capturedAt });
	}, [accuracy, capturedAt]);

	if (loading) {
		return (
			<Container>
				<StatusIcon status='loading'>
					<LoadingSpinner />
				</StatusIcon>
				<ContentWrapper>
					<Title>Requesting Location Access</Title>
					<Description>Please allow location access when prompted by your browser.</Description>
				</ContentWrapper>
			</Container>
		);
	}

	if (error) {
		return (
			<Container status='error'>
				<StatusIcon status='error'>
					<ErrorIcon />
				</StatusIcon>
				<ContentWrapper>
					<Title status='error'>Location Access Required</Title>
					<Description>{error}</Description>
					<RetryButton
						type='button'
						onClick={retry}
					>
						Try Again
					</RetryButton>
				</ContentWrapper>
			</Container>
		);
	}

	if (latitude !== null && longitude !== null) {
		if (hasPollingUnit) {
			return (
				<Container
					status='success'
					compact
				>
					<StatusIcon
						status='success'
						compact
					>
						<SuccessIcon />
					</StatusIcon>
					<ContentWrapper>
						<Title status='success'>Location captured{formatAccuracy(accuracy)}</Title>
					</ContentWrapper>
				</Container>
			);
		}

		return (
			<Container status='success'>
				<StatusIcon status='success'>
					<SuccessIcon />
				</StatusIcon>
				<ContentWrapper>
					<Title status='success'>Location Captured</Title>
					<CoordinatesWrapper>
						<CoordinateItem>
							<CoordinateLabel>Latitude</CoordinateLabel>
							<CoordinateValue>{formatCoordinate(latitude, 'lat')}</CoordinateValue>
						</CoordinateItem>
						<CoordinateItem>
							<CoordinateLabel>Longitude</CoordinateLabel>
							<CoordinateValue>{formatCoordinate(longitude, 'lng')}</CoordinateValue>
						</CoordinateItem>
					</CoordinatesWrapper>
				</ContentWrapper>
			</Container>
		);
	}

	return null;
};

// Styled Components
const Container = styled(domTag('div', ['status', 'compact']))(
	({ status, compact = false }: { status?: 'success' | 'error'; compact?: boolean }) => ({
		display: 'flex',
		alignItems: compact ? 'center' : 'flex-start',
		gap: compact ? '0.75rem' : '1rem',
		padding: compact ? '0.625rem 0.875rem' : '1rem',
		borderRadius: '8px',
		marginBottom: '1.5rem',
		border: '1px solid',
		borderColor:
			status === 'success' ? 'var(--color-success)' : status === 'error' ? 'var(--color-error)' : 'var(--color-border-default)',
		backgroundColor:
			status === 'success'
				? 'var(--color-success-light, rgba(16, 185, 129, 0.1))'
				: status === 'error'
					? 'var(--color-error-light, rgba(239, 68, 68, 0.1))'
					: 'var(--color-bg-secondary)',
	})
);

const StatusIcon = styled(domTag('div', ['status', 'compact']))(
	({ status, compact = false }: { status: 'loading' | 'success' | 'error'; compact?: boolean }) => ({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: compact ? '28px' : '40px',
		height: compact ? '28px' : '40px',
		borderRadius: '50%',
		flexShrink: 0,
		backgroundColor:
			status === 'success' ? 'var(--color-success)' : status === 'error' ? 'var(--color-error)' : 'var(--color-brand-primary)',
		color: '#fff',
	})
);

const ContentWrapper = styled('div')({
	flex: 1,
	minWidth: 0,
	display: 'flex',
	flexDirection: 'column',
	gap: '0.5rem',
});

const Title = styled(domTag('h4', ['status']))(({ status }: { status?: 'success' | 'error' }) => ({
	margin: 0,
	fontFamily: 'var(--font-family-medium)',
	fontSize: 'var(--text-body-size)',
	lineHeight: 'var(--leading-snug)',
	color: status === 'success' ? 'var(--color-success)' : status === 'error' ? 'var(--color-error)' : 'var(--color-text-primary)',
}));

const Description = styled('p')({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--text-body-sm-size)',
	lineHeight: 'var(--leading-normal)',
	color: 'var(--color-text-tertiary)',
});

const CoordinatesWrapper = styled('div')({
	display: 'flex',
	flexWrap: 'wrap',
	gap: '0.75rem 2rem',
	marginTop: '0.5rem',
});

const CoordinateItem = styled('div')({
	display: 'flex',
	flexDirection: 'column',
	gap: '0.25rem',
});

const CoordinateLabel = styled('span')({
	fontFamily: 'var(--font-family-medium)',
	fontSize: 'var(--type-xs)',
	lineHeight: 'var(--leading-normal)',
	color: 'var(--color-text-tertiary)',
	textTransform: 'uppercase',
	letterSpacing: 'var(--tracking-wider)',
});

const CoordinateValue = styled('span')({
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--text-body-size)',
	lineHeight: 'var(--leading-normal)',
	color: 'var(--color-text-primary)',
});

const RetryButton = styled('button')({
	'alignSelf': 'flex-start',
	'marginTop': '0.5rem',
	'padding': '0.5rem 1rem',
	'minHeight': 44,
	'border': '1px solid var(--color-error)',
	'borderRadius': '5px',
	'backgroundColor': 'transparent',
	'color': 'var(--color-error)',
	'fontFamily': 'var(--font-family-medium)',
	'fontSize': 'var(--text-body-sm-size)',
	'cursor': 'pointer',
	'transition': 'background-color 0.2s ease',
	'&:hover': {
		backgroundColor: 'var(--color-error)',
		color: '#fff',
	},
});

// SVG Icons
const LoadingSpinner: React.FC = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		style={{ animation: 'spin 1s linear infinite' }}
	>
		<style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
		<circle
			cx='12'
			cy='12'
			r='10'
			stroke='currentColor'
			strokeWidth='3'
			strokeLinecap='round'
			strokeDasharray='31.4 31.4'
		/>
	</svg>
);

const SuccessIcon: React.FC = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
	>
		<path
			d='M20 6L9 17L4 12'
			stroke='currentColor'
			strokeWidth='2.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const ErrorIcon: React.FC = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
	>
		<path
			d='M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

export default GeolocationCapture;
